import { getKnownViewModelFieldNames } from "../../Engine/src/Engine/Core/CoreTypes";
import { getSimpleHashByString } from "../hashUtils";
import { IterUtils } from "../IterableUtils";
import { isNotNullOrEmpty } from "../TypingUtils";

import { IEquatable } from "./IEquatable";
import { ViewModelFieldNameWithCustomFields } from "./AbsoluteModelFieldPath";
import { EachCurrentCollision } from "./EachCurrentCollision";
import { ModelPath, PathTokens } from "./ModelPath";

export abstract class BaseModelFieldPath<T extends ModelPath> implements IEquatable<BaseModelFieldPath<T>> {
    protected static readonly extraFieldNames = ["\\$errorCount", "\\$warningCount"];
    protected static readonly fieldExtractRegex = new RegExp(
        `^(.*)\\.(${BaseModelFieldPath.extraFieldNames.concat(...getKnownViewModelFieldNames().values()).join("|")})$`
    );
    public readonly path: T;
    public readonly field: ViewModelFieldNameWithCustomFields;

    protected constructor(path: T, fieldName: ViewModelFieldNameWithCustomFields) {
        this.path = path;
        this.field = fieldName;
    }

    public getHash(): number {
        return this.path.getHash() + getSimpleHashByString(this.field);
    }

    public isEquals(
        modelPath: BaseModelFieldPath<T>,
        eachCurrentCollision: EachCurrentCollision = EachCurrentCollision.AreSame
    ): boolean {
        return this.path.isEquals(modelPath.path, eachCurrentCollision) && this.field === modelPath.field;
    }

    public toString(includeLeadingSlash: boolean = false): string {
        return `${includeLeadingSlash ? "/" : ""}${this.path.toLegacyPath()}.${this.field}`;
    }

    public toLegacyPath(addSlashIfLastTokenIsInstanceOrMultiplicity: boolean = true): string {
        const lastToken = IterUtils.last(this.path.getPathParts());
        const isLastTokenAnInstanceOrMultiplicity =
            PathTokens.isInstanceTokenOrInstancesSpec(lastToken) || PathTokens.isMultiToken(lastToken);
        const slashIfRequired =
            addSlashIfLastTokenIsInstanceOrMultiplicity &&
            isLastTokenAnInstanceOrMultiplicity &&
            isNotNullOrEmpty(this.field)
                ? "/"
                : "";
        const dotIfRequired = isNotNullOrEmpty(this.field) ? "." : "";
        return `${this.path.toLegacyPath()}${slashIfRequired}${dotIfRequired}${this.field}`;
    }
}
